/* eslint-disable no-unused-vars */
/* eslint-disable no-param-reassign */
/* eslint no-shadow: ["error", { "allow": ["state", "getters"] }] */
import { cloneDeep, set } from 'lodash-es';
import {
  modifySubscription,
  getSubscriptionFromId,
  validateSubscriptionDeclaration as _validateSubscriptionDeclaration,
  findAmendmentChangesBySubscriptionId,
  deleteAmendment as _deleteAmendment,
  applyAmendment as _applyAmendment,
} from '@/services/api/buy/subscription';
import {
  createAmendmentFromPolicy,
  findPolicyAmendments,
  findPolicyAmendmentDiff,
  getPolicyFromId,
  updatePolicyRisk as _updatePolicyRisk,
} from '@/services/api/buy/policy';
import {
  buildAmendmentPricing,
  fetchDocumentsForEntity,
  resolvePendingAmendment,
  resolvePolicyId,
  resolveReferenceSubscriptionId,
} from './contract.helpers';
import { createContractState } from './contract.state';

const state = createContractState();
const UPDATE_ERROR_MESSAGE = 'Les modifications apportées à votre contrat ne nous permettent pas de calculer un tarif. Merci de vous rapprocher de notre équipe souscription.';

const getters = {
  get: (state) => state.current, current: (state) => state.current,
  policyContext: (state) => state.policyContext,
  referenceSubscription: (state) => state.referenceSubscription,
  currentPolicyId: (state) => resolvePolicyId(state.current, state.policyContext),
  amendmentPricing: (state) => buildAmendmentPricing(state.current, state.referenceSubscription),
  pendingAmendment: (state) => state.pendingAmendment,
  isCreatingAmendment: (state) => state.isCreatingAmendment,
  amendmentModification: (state) => state.amendmentModification,
  isApplyingAmendment: (state) => state.isApplyingAmendment,
  amendmentApplyError: (state) => state.amendmentApplyError,
  amendmentFieldErrors: (state) => state.amendmentFieldErrors,
};

const actions = {
  clear({ commit }) {
    commit('CLEAR');
  },
  setValue({ commit }, object) {
    commit('setValue', object);
  },
  setValues({ commit }, object) {
    commit('setValues', object);
    return Promise.resolve();
  },
  async setSubscription({ commit }, subscription) {
    commit('setValues', { current: subscription, referenceSubscription: null });
    return Promise.resolve(subscription.id);
  },
  async hydratePolicyContext({ commit }, policyId) {
    if (!policyId) {
      commit('setValue', { policyContext: null });
      return null;
    }

    const { data: policy } = await getPolicyFromId(policyId);
    commit('setValue', { policyContext: policy });
    return policy;
  },
  async hydrateReferenceSubscription({ commit }, subscriptionId) {
    if (!subscriptionId) {
      commit('setValue', { referenceSubscription: null });
      return null;
    }

    const { data: referenceSubscription } = await getSubscriptionFromId(subscriptionId);
    commit('setValue', { referenceSubscription });
    return referenceSubscription;
  },
  async syncReferenceSubscription({ state, commit, dispatch }, currentSubscription = state.current) {
    const referenceSubscriptionId = resolveReferenceSubscriptionId(currentSubscription, state.policyContext);
    if (!referenceSubscriptionId) {
      commit('setValue', { referenceSubscription: null });
      return null;
    }

    if (state.referenceSubscription && state.referenceSubscription.id === referenceSubscriptionId) {
      return state.referenceSubscription;
    }

    return dispatch('hydrateReferenceSubscription', referenceSubscriptionId);
  },
  async getSubscription({ dispatch }, id) {
    try {
      const { data: subscription } = await getSubscriptionFromId(id);
      const documents = await fetchDocumentsForEntity(subscription);
      return dispatch('setSubscription', Object.assign(subscription, { documents }));
    } catch (err) {
      return Promise.resolve(id);
    }
  },
  async getSubscriptionDetail({ commit, dispatch }, id) {
    const { data: subscription } = await getSubscriptionFromId(id);
    const documents = await fetchDocumentsForEntity(subscription);
    const currentSubscription = Object.assign({}, subscription, { documents });

    commit('setValues', {
      current: currentSubscription,
      referenceSubscription: null,
      pendingAmendment: null,
    });

    if (subscription.policyId) {
      try {
        await dispatch('hydratePolicyContext', subscription.policyId);
        await dispatch('loadPendingAmendment', subscription.policyId);
        await dispatch('syncReferenceSubscription', currentSubscription);
      } catch (err) {
        commit('setValues', { policyContext: null, pendingAmendment: null, referenceSubscription: null });
      }
    } else {
      commit('setValues', {
        policyContext: null,
        referenceSubscription: null,
        pendingAmendment: null,
      });
    }

    return currentSubscription;
  },
  async getContractDetail({ commit, dispatch }, payload) {
    const params = typeof payload === 'string'
      ? { id: payload, entity: null }
      : payload;
    const { id, entity } = params;

    if (entity === 'subscription') {
      return dispatch('getSubscriptionDetail', id);
    }

    try {
      const { data: policy } = await getPolicyFromId(id);
      const documents = await fetchDocumentsForEntity(policy);
      const currentPolicy = Object.assign({}, policy, { documents });

      commit('setValues', {
        current: currentPolicy,
        policyContext: policy,
        referenceSubscription: null,
        pendingAmendment: null,
      });

      await dispatch('loadPendingAmendment', policy.id);
      return currentPolicy;
    } catch (policyError) {
      const status = policyError.response && policyError.response.status;
      if (![400, 404].includes(status)) {
        throw policyError;
      }

      return dispatch('getSubscriptionDetail', id);
    }
  },
  async loadPendingAmendment({ commit, state }, policyId) {
    const resolvedPolicyId = policyId || resolvePolicyId(state.current, state.policyContext);
    if (!resolvedPolicyId) {
      commit('setValue', { pendingAmendment: null });
      return null;
    }

    const { data: amendmentsPayload } = await findPolicyAmendments(resolvedPolicyId);
    const pendingAmendment = resolvePendingAmendment(amendmentsPayload);
    commit('setValue', { pendingAmendment });
    return pendingAmendment;
  },
  async updateSubscription({ commit, dispatch, state }, { value, path }) {
    try {
      const subscription = state.current;
      if (subscription) {
        await dispatch('contracts/setValue', { updateError: null }, { root: true });
        const nextSubscription = cloneDeep(subscription);
        set(nextSubscription, path, value);
        const {
          data: updatedSubscription,
        } = await modifySubscription(nextSubscription.id, nextSubscription);
        commit('SET_SUBSCRIPTION', updatedSubscription);
        return updatedSubscription;
      }
      return subscription;
    } catch (err) {
      if (err.response && err.response.status === 422) {
        await dispatch('contracts/setValue', { updateError: UPDATE_ERROR_MESSAGE }, { root: true });
      }
      dispatch('reporting/sendError', { err, currentPage: 'Amendment' }, { root: true });
      throw err;
    }
  },
  async updatePolicyRisk({ commit, dispatch, state }, riskPatch) {
    const policyId = resolvePolicyId(state.current, state.policyContext);
    if (!policyId) {
      throw new Error('Policy id is required to update risk');
    }

    try {
      await dispatch('contracts/setValue', { updateError: null }, { root: true });
      const { data } = await _updatePolicyRisk(policyId, riskPatch);
      const currentRisk = state.current.risk || {};
      const responseRisk = data && data.risk ? data.risk : null;
      const nextRisk = responseRisk || Object.assign({}, currentRisk, riskPatch);

      commit('SET_SUBSCRIPTION', Object.assign({}, state.current, {
        risk: Object.assign({}, currentRisk, nextRisk),
      }));

      if (data && data.id === policyId) {
        commit('setValue', { policyContext: data });
      }

      return data;
    } catch (err) {
      if (err.response && err.response.status === 422) {
        await dispatch('contracts/setValue', { updateError: UPDATE_ERROR_MESSAGE }, { root: true });
      }
      dispatch('reporting/sendError', { err, currentPage: 'ComplementaryInformation' }, { root: true });
      throw err;
    }
  },
  async updateEffectiveDateAmendment({ dispatch }, { value }) {
    return dispatch('updateSubscription', { value, path: 'effectiveDate' });
  },
  async deleteAmendment({ state, commit, dispatch }, payload = {}) {
    const subscriptionId = payload.subscriptionId || state.current.id;
    if (!subscriptionId) {
      return null;
    }

    const { data: revertedSubscription } = await _deleteAmendment({ subscriptionId });
    commit('SET_SUBSCRIPTION', revertedSubscription);

    if (revertedSubscription.policyId) {
      try {
        await dispatch('hydratePolicyContext', revertedSubscription.policyId);
        await dispatch('loadPendingAmendment', revertedSubscription.policyId);
      } catch (err) {
        // Keep the reverted subscription visible even if policy refresh fails.
      }
    }

    await dispatch('syncReferenceSubscription', revertedSubscription);

    return revertedSubscription;
  },
  async createAmendment({ state, commit }, policyId) {
    const resolvedPolicyId = policyId || resolvePolicyId(state.current, state.policyContext);
    if (!resolvedPolicyId || state.isCreatingAmendment) {
      return null;
    }

    commit('setValue', { isCreatingAmendment: true });
    try {
      const { data: amendment } = await createAmendmentFromPolicy(resolvedPolicyId);
      commit('setValue', { pendingAmendment: amendment });
      return amendment;
    } finally {
      commit('setValue', { isCreatingAmendment: false });
    }
  },
  async findChanges({ state, commit }, id) {
    const subscriptionId = id || state.current.id;
    if (!subscriptionId) {
      commit('setValue', { amendmentModification: [] });
      return [];
    }

    let currentSubscription = state.current;
    if (!currentSubscription.id || currentSubscription.id !== subscriptionId) {
      const { data: subscription } = await getSubscriptionFromId(subscriptionId);
      currentSubscription = subscription;
    }

    if (currentSubscription.status === 'AMENDMENT') {
      const { data: changes } = await findPolicyAmendmentDiff(subscriptionId);
      commit('setValue', { amendmentModification: changes || [] });
      return changes || [];
    }

    const {
      data: amendmentModificationArray,
    } = await findAmendmentChangesBySubscriptionId(subscriptionId);
    if (amendmentModificationArray.length > 0) {
      const { changes } = amendmentModificationArray.pop();
      commit('setValue', { amendmentModification: changes });
      return changes;
    }

    commit('setValue', { amendmentModification: [] });
    return [];
  },
  validateSubscriptionDeclaration({ state }) {
    const { id } = state.current;
    return _validateSubscriptionDeclaration(id);
  },
  async applyAmendment({ state, commit, dispatch }, { effectiveDate, subscriptionId: payloadSubscriptionId }) {
    const subscriptionId = payloadSubscriptionId || state.current.id;
    if (!subscriptionId || state.isApplyingAmendment) return null;
    commit('setValues', { isApplyingAmendment: true, amendmentApplyError: '', amendmentFieldErrors: {} });
    try {
      const { data: subscription } = await _applyAmendment(subscriptionId, effectiveDate);
      commit('SET_SUBSCRIPTION', subscription);

      if (subscription.policyId) {
        try {
          await dispatch('hydratePolicyContext', subscription.policyId);
          await dispatch('loadPendingAmendment', subscription.policyId);
        } catch (err) {
          // Keep the updated subscription even if policy refresh fails.
        }
      }

      await dispatch('syncReferenceSubscription', subscription);

      return subscription;
    } catch (error) {
      const status = error.response && error.response.status;
      const message = (error.response && error.response.data && error.response.data.message) || 'Une erreur est survenue.';
      if (status === 400) {
        commit('setValues', { amendmentFieldErrors: { effectiveDate: message } });
      } else if (status === 422) {
        commit('setValues', { amendmentApplyError: message });
        throw error;
      } else {
        commit('setValues', { amendmentApplyError: message });
        await dispatch('getSubscription', state.current.id);
        throw error;
      }
    } finally {
      commit('setValues', { isApplyingAmendment: false });
    }
  },
};

const mutations = {
  CLEAR(state) {
    Object.assign(state, createContractState());
  },
  SET_SUBSCRIPTION(state, subscription) {
    state.current = subscription;
  },
  SET_ID(state, id) {
    state.id = id;
  },
  SET_REFERENCE(state, reference) {
    state.reference = reference;
  },
  setValue(state, object) {
    const [key] = Object.keys(object);
    state[key] = object[key];
  },
  SET_VALUE_WITH_PATH(state, { value, path }) {
    set(state, path, value);
  },
  setValues(state, object) {
    Object.assign(state, { ...object });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
